import type {
  Annotation,
  AnnotationKind,
  RevealStep,
  ScriptElement,
  Slide,
  TeachingPlanCue,
  TeachingPlanStep,
} from './schema.types';
import { uid } from './utils';

/** Flatten a slide's elements into the `{id,type,summary}` shape the
 *  `/ai/generate-teaching` endpoint expects. */
export function summarizeSlideElements(
  slide: Slide,
): { id: string; type: string; summary: string }[] {
  const out: { id: string; type: string; summary: string }[] = [];
  for (const row of slide.rows) {
    for (const col of row.columns) {
      for (const el of col.elements) {
        out.push({ id: el.id, type: el.type, summary: summarizeElement(el) });
      }
    }
  }
  return out;
}

function summarizeElement(el: ScriptElement): string {
  const c = el.content as Record<string, unknown> | string | undefined;
  if (typeof c === 'string') return stripHtml(c).slice(0, 200);
  if (!c) return el.type;
  if (el.type === 'mcq') {
    const q = String((c as { question?: string }).question ?? '');
    const opts = ((c as { options?: string[] }).options ?? []).join(' / ');
    return `Q: ${q} — options: ${opts}`.slice(0, 300);
  }
  if (el.type === 'callout') return String((c as { body?: string }).body ?? 'callout').slice(0, 200);
  if (el.type === 'icon') return `icon: ${String((c as { id?: string }).id ?? '')}`;
  if (el.type === 'image') return `image: ${String((c as { alt?: string }).alt ?? 'figure')}`;
  if (el.type === 'latex') return `formula: ${String((c as { source?: string }).source ?? '')}`.slice(0, 200);
  return el.type;
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

const CUE_TO_ANNOTATION: Partial<Record<TeachingPlanCue['kind'], AnnotationKind>> = {
  highlight: 'highlight',
  underline: 'underline',
  encircle: 'encircle',
  annotate: 'annotate',
};

function cueToAnnotation(cue: TeachingPlanCue, targetElementId: string): Annotation | null {
  let kind: AnnotationKind | undefined = CUE_TO_ANNOTATION[cue.kind];
  // `draw` cues carry no geometry at apply-time, so map them to the closest
  // element-relative annotation (the teacher can refine with the pen tool).
  if (cue.kind === 'draw') kind = cue.drawShape === 'circle' ? 'encircle' : 'underline';
  if (!kind) return null;
  const colors: Record<AnnotationKind, string> = {
    highlight: '#facc15',
    encircle: '#ef4444',
    underline: '#3b82f6',
    annotate: '#6366f1',
    zoom: '#f59e0b',
    spotlight: '#f59e0b',
  };
  return {
    id: uid('an'),
    kind,
    targetElementId,
    color: colors[kind],
    text: kind === 'annotate' ? cue.text : undefined,
  };
}

/**
 * Convert an AI teaching plan into reveal steps: one step per plan step (in the
 * planned reveal order), each revealing its element with the spoken narration
 * text and any emphasis cues mapped to annotations.
 *
 * AUDIO-FREE: this only writes narration TEXT into `audio.text` (the field the
 * existing VoxCPM pipeline later voices). It never sets `audio.src` / generates
 * audio.
 */
export function buildRevealStepsFromTeachingPlan(steps: TeachingPlanStep[]): RevealStep[] {
  return steps.map((s) => {
    const annotations = s.cues
      .map((c) => cueToAnnotation(c, s.elementId))
      .filter((a): a is Annotation => a !== null);
    const step: RevealStep = {
      id: uid('step'),
      label: 'AI step',
      elementIds: [s.elementId],
      elementAnimations: { [s.elementId]: { animation: 'fade', durationMs: 400, delayMs: 0 } },
      annotations,
      takeInput: s.takeInput || undefined,
    };
    if (s.speechPrompt.trim()) {
      step.audio = { text: s.speechPrompt.trim(), source: 'tts' };
    }
    return step;
  });
}

/**
 * Replace a slide's reveal steps with those built from a teaching plan.
 * Removes existing steps, then inserts the new ones in order. Audio is NOT
 * generated here — the teacher voices the narration via the existing flow.
 */
export function applyTeachingPlanToStore(args: {
  slideId: string;
  existingStepIds: string[];
  steps: TeachingPlanStep[];
  actions: {
    removeRevealStep: (slideId: string, stepId: string) => void;
    upsertRevealStep: (slideId: string, step: RevealStep, index?: number) => void;
  };
}): RevealStep[] {
  const { slideId, existingStepIds, steps, actions } = args;
  for (const id of existingStepIds) actions.removeRevealStep(slideId, id);
  const built = buildRevealStepsFromTeachingPlan(steps);
  built.forEach((step, i) => actions.upsertRevealStep(slideId, step, i));
  return built;
}
